import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../internal/cx';
import { useFieldProps } from './fieldContext';
import styles from './Form.module.css';

export type ControlSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Decorative leading icon. */
  leftIcon?: ReactNode;
  /** Trailing element inside the control (e.g. a unit label or icon button). */
  rightElement?: ReactNode;
  /** Class for the outer control wrapper (className goes on the <input>). */
  wrapperClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

export function TextInput({
  size = 'md',
  invalid: invalidProp,
  leftIcon,
  rightElement,
  wrapperClassName,
  className,
  id: idProp,
  required: requiredProp,
  disabled: disabledProp,
  readOnly,
  type = 'text',
  'aria-describedby': ariaDescribedBy,
  ref,
  ...rest
}: TextInputProps) {
  const field = useFieldProps({
    id: idProp,
    'aria-describedby': ariaDescribedBy,
    invalid: invalidProp,
    required: requiredProp,
    disabled: disabledProp,
  });

  return (
    <div
      className={cx(
        styles.control,
        styles[`control-${size}`],
        field.invalid && styles.controlInvalid,
        field.disabled && styles.controlDisabled,
        readOnly && styles.controlReadOnly,
        wrapperClassName,
      )}
    >
      {leftIcon && (
        <span className={styles.adornment} aria-hidden="true">
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        id={field.id}
        className={cx(styles.input, className)}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        required={field.required || undefined}
        disabled={field.disabled || undefined}
        readOnly={readOnly}
        {...rest}
      />
      {rightElement && <span className={styles.trailing}>{rightElement}</span>}
    </div>
  );
}
